import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  CTAGroup,
  ReorderButtons,
  SelectInput,
} from "../../../components/FormField";

import { MediaImageField } from "../../../components/MediaPickerDialog";
import { emptyButton, moveItem } from "./shared";
import { ICON_OPTIONS } from "../../../lib/iconsData";

export function CareersSections({ setSectionsData, initialData }) {
  /* ---------------- HERO ---------------- */
  const [heroActive, setHeroActive] = useState(true);
  const [heroBadgeText, setHeroBadgeText] = useState("Join Our Team");
  const [heroHeading, setHeroHeading] = useState("Build Your Career at");
  const [heroHeadingEmphasis, setHeroHeadingEmphasis] =
    useState("ThynkProcess");
  const [heroDescription, setHeroDescription] = useState("");
  const [heroPrimary, setHeroPrimary] = useState(emptyButton());
  const [heroSecondary, setHeroSecondary] = useState(emptyButton());

  /* ---------------- WHY THYNKPROCESS ---------------- */
  const [whyActive, setWhyActive] = useState(true);
  const [whyBadgeText, setWhyBadgeText] = useState("Why ThynkProcess");
  const [whyHeading, setWhyHeading] = useState("Why Work at");
  const [whyHeadingEmphasis, setWhyHeadingEmphasis] = useState("ThynkProcess?");
  const [whyDescription, setWhyDescription] = useState(
    "We invest in our people the same way we invest in our clients.",
  );
  const [whyCards, setWhyCards] = useState([
    { icon: "", title: "", description: "" },
  ]);

  /* ---------------- CULTURE ---------------- */
  const [cultureActive, setCultureActive] = useState(true);
  const [cultureBadgeText, setCultureBadgeText] = useState("Our Culture");
  const [cultureHeading, setCultureHeading] = useState("A Flat, Meritocratic");
  const [cultureHeadingEmphasis, setCultureHeadingEmphasis] =
    useState("Organisation");
  const [cultureParagraphs, setCultureParagraphs] = useState(["", "", ""]);
  const [cultureImage, setCultureImage] = useState("");
  const [cultureImageAlt, setCultureImageAlt] = useState(
    "ThynkProcess team culture",
  );

  /* ---------------- HIRING PROCESS ---------------- */
  const [hiringActive, setHiringActive] = useState(true);
  const [hiringBadgeText, setHiringBadgeText] = useState("How It Works");
  const [hiringHeading, setHiringHeading] = useState("Our");
  const [hiringHeadingEmphasis, setHiringHeadingEmphasis] =
    useState("Hiring Process");
  const [hiringDescription, setHiringDescription] = useState(
    "Typical process: 2–3 weeks from application to offer.",
  );
  const [hiringSteps, setHiringSteps] = useState([
    { step: "01", title: "", description: "" },
  ]);

  /* ---------------- APPLY SECTION ---------------- */
  const [applyActive, setApplyActive] = useState(true);
  const [applyBadgeText, setApplyBadgeText] = useState("Apply Now");
  const [applyHeading, setApplyHeading] = useState("Submit Your");
  const [applyHeadingEmphasis, setApplyHeadingEmphasis] =
    useState("Application");
  const [applyDescription, setApplyDescription] = useState(
    "Fill in the form below and we'll be in touch within 2–3 business days.",
  );

  /* ---------------- SIDEBAR ---------------- */
  const [sidebarActive, setSidebarActive] = useState(true);
  const [sidebarCareersHeading, setSidebarCareersHeading] =
    useState("Careers Team");
  const [sidebarCareersSubtext, setSidebarCareersSubtext] = useState(
    "Prefer to reach out directly? We'd love to hear from you.",
  );
  const [sidebarEmail, setSidebarEmail] = useState(
    "careers@thynkprocess.com.au",
  );
  const [sidebarEmailSub, setSidebarEmailSub] = useState(
    "We respond within 2–3 business days",
  );
  const [sidebarPhone, setSidebarPhone] = useState("(03) 7075 3900");
  const [sidebarPhoneSub, setSidebarPhoneSub] = useState(
    "Mon–Fri, 9 AM–5:30 PM AEST",
  );
  const [sidebarOffersHeading, setSidebarOffersHeading] =
    useState("What We Offer");
  const [sidebarOfferItems, setSidebarOfferItems] = useState([
    "Competitive salary + performance bonuses",
    "Hybrid / remote working arrangements",
    "SAP & Salesforce certification funding",
    "Professional development budget",
    "Career progression from within",
    "Collaborative, no-politics culture",
  ]);
  const [sidebarSpecHeading, setSidebarSpecHeading] = useState(
    "Don't See the Right Role?",
  );
  const [sidebarSpecText, setSidebarSpecText] = useState(
    'Select "Speculative Application" in the form — we\'re always keen to hear from exceptional talent.',
  );
  const [sidebarSpecCTA, setSidebarSpecCTA] = useState(
    "Send Speculative Application",
  );

  /* ---------------- FINAL CTA ---------------- */
  const [ctaActive, setCtaActive] = useState(true);
  const [ctaHeading, setCtaHeading] = useState("Ready to Do the Best Work");
  const [ctaHeadingEmphasis, setCtaHeadingEmphasis] =
    useState("of Your Career?");
  const [ctaDescription, setCtaDescription] = useState("");
  const [ctaPrimary, setCtaPrimary] = useState(emptyButton());
  const [ctaSecondary, setCtaSecondary] = useState(emptyButton());

  const updateBtn = (setter, key, val) => setter((b) => ({ ...b, [key]: val }));

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;

    if (d.hero) {
      setHeroBadgeText(d.hero.badge_text || "");
      setHeroHeading(d.hero.heading || "");
      setHeroHeadingEmphasis(d.hero.heading_emphasis || "");
      setHeroDescription(d.hero.description || "");
      if (d.hero.primary_cta)
        setHeroPrimary({
          text: d.hero.primary_cta.text || "",
          url: d.hero.primary_cta.link || "",
          newTab: false,
        });
      if (d.hero.secondary_cta)
        setHeroSecondary({
          text: d.hero.secondary_cta.text || "",
          url: d.hero.secondary_cta.link || "",
          newTab: false,
        });
    }

    if (d.why_thynkprocess) {
      setWhyBadgeText(d.why_thynkprocess.badge_text || "");
      setWhyHeading(d.why_thynkprocess.heading || "");
      setWhyHeadingEmphasis(d.why_thynkprocess.heading_emphasis || "");
      setWhyDescription(d.why_thynkprocess.description || "");
      if (d.why_thynkprocess.cards?.length)
        setWhyCards(d.why_thynkprocess.cards);
    }

    if (d.culture) {
      setCultureBadgeText(d.culture.badge_text || "");
      setCultureHeading(d.culture.heading || "");
      setCultureHeadingEmphasis(d.culture.heading_emphasis || "");
      if (d.culture.paragraphs?.length)
        setCultureParagraphs(d.culture.paragraphs);
      setCultureImage(d.culture.image || "");
      setCultureImageAlt(d.culture.image_alt || "");
    }

    if (d.hiring_process) {
      setHiringBadgeText(d.hiring_process.badge_text || "");
      setHiringHeading(d.hiring_process.heading || "");
      setHiringHeadingEmphasis(d.hiring_process.heading_emphasis || "");
      setHiringDescription(d.hiring_process.description || "");
      if (d.hiring_process.steps?.length)
        setHiringSteps(d.hiring_process.steps);
    }

    if (d.apply_section) {
      setApplyBadgeText(d.apply_section.badge_text || "");
      setApplyHeading(d.apply_section.heading || "");
      setApplyHeadingEmphasis(d.apply_section.heading_emphasis || "");
      setApplyDescription(d.apply_section.description || "");
    }

    if (d.sidebar) {
      setSidebarCareersHeading(d.sidebar.careers_heading || "");
      setSidebarCareersSubtext(d.sidebar.careers_subtext || "");
      setSidebarEmail(d.sidebar.careers_email || "");
      setSidebarEmailSub(d.sidebar.careers_email_sub || "");
      setSidebarPhone(d.sidebar.careers_phone || "");
      setSidebarPhoneSub(d.sidebar.careers_phone_sub || "");
      setSidebarOffersHeading(d.sidebar.offers_heading || "");
      if (d.sidebar.offer_items?.length)
        setSidebarOfferItems(d.sidebar.offer_items);
      setSidebarSpecHeading(d.sidebar.speculative_heading || "");
      setSidebarSpecText(d.sidebar.speculative_text || "");
      setSidebarSpecCTA(d.sidebar.speculative_cta || "");
    }

    if (d.final_cta) {
      setCtaHeading(d.final_cta.heading || "");
      setCtaHeadingEmphasis(d.final_cta.heading_emphasis || "");
      setCtaDescription(d.final_cta.description || "");
      if (d.final_cta.primary_cta)
        setCtaPrimary({
          text: d.final_cta.primary_cta.text || "",
          url: d.final_cta.primary_cta.link || "",
          newTab: false,
        });
      if (d.final_cta.secondary_cta)
        setCtaSecondary({
          text: d.final_cta.secondary_cta.text || "",
          url: d.final_cta.secondary_cta.link || "",
          newTab: false,
        });
    }
  }, [initialData]);

  const buildPayload = () => ({
    hero: {
      badge_text: heroBadgeText,
      heading: heroHeading,
      heading_emphasis: heroHeadingEmphasis,
      description: heroDescription,
      primary_cta: { text: heroPrimary.text, link: heroPrimary.url },
      secondary_cta: { text: heroSecondary.text, link: heroSecondary.url },
    },
    why_thynkprocess: {
      badge_text: whyBadgeText,
      heading: whyHeading,
      heading_emphasis: whyHeadingEmphasis,
      description: whyDescription,
      cards: whyCards,
    },
    culture: {
      badge_text: cultureBadgeText,
      heading: cultureHeading,
      heading_emphasis: cultureHeadingEmphasis,
      paragraphs: cultureParagraphs,
      image: cultureImage,
      image_alt: cultureImageAlt,
    },
    hiring_process: {
      badge_text: hiringBadgeText,
      heading: hiringHeading,
      heading_emphasis: hiringHeadingEmphasis,
      description: hiringDescription,
      steps: hiringSteps,
    },
    apply_section: {
      badge_text: applyBadgeText,
      heading: applyHeading,
      heading_emphasis: applyHeadingEmphasis,
      description: applyDescription,
    },
    sidebar: {
      careers_heading: sidebarCareersHeading,
      careers_subtext: sidebarCareersSubtext,
      careers_email: sidebarEmail,
      careers_email_sub: sidebarEmailSub,
      careers_phone: sidebarPhone,
      careers_phone_sub: sidebarPhoneSub,
      offers_heading: sidebarOffersHeading,
      offer_items: sidebarOfferItems,
      speculative_heading: sidebarSpecHeading,
      speculative_text: sidebarSpecText,
      speculative_cta: sidebarSpecCTA,
    },
    final_cta: {
      heading: ctaHeading,
      heading_emphasis: ctaHeadingEmphasis,
      description: ctaDescription,
      primary_cta: { text: ctaPrimary.text, link: ctaPrimary.url },
      secondary_cta: { text: ctaSecondary.text, link: ctaSecondary.url },
    },
  });

  useEffect(() => {
    setSectionsData?.(buildPayload());
  }, [
    heroBadgeText,
    heroHeading,
    heroHeadingEmphasis,
    heroDescription,
    heroPrimary,
    heroSecondary,
    whyBadgeText,
    whyHeading,
    whyHeadingEmphasis,
    whyDescription,
    whyCards,
    cultureBadgeText,
    cultureHeading,
    cultureHeadingEmphasis,
    cultureParagraphs,
    cultureImage,
    cultureImageAlt,
    hiringBadgeText,
    hiringHeading,
    hiringHeadingEmphasis,
    hiringDescription,
    hiringSteps,
    applyBadgeText,
    applyHeading,
    applyHeadingEmphasis,
    applyDescription,
    sidebarCareersHeading,
    sidebarCareersSubtext,
    sidebarEmail,
    sidebarEmailSub,
    sidebarPhone,
    sidebarPhoneSub,
    sidebarOffersHeading,
    sidebarOfferItems,
    sidebarSpecHeading,
    sidebarSpecText,
    sidebarSpecCTA,
    ctaHeading,
    ctaHeadingEmphasis,
    ctaDescription,
    ctaPrimary,
    ctaSecondary,
  ]);

  return (
    <>
      {/* HERO */}
      <CollapsibleSection
        title="Hero Section"
        active={heroActive}
        onToggleActive={setHeroActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={heroBadgeText}
            onChange={(e) => setHeroBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={heroHeading}
            onChange={(e) => setHeroHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={heroHeadingEmphasis}
            onChange={(e) => setHeroHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={heroDescription}
            onChange={(e) => setHeroDescription(e.target.value)}
          />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={heroPrimary.text}
          url={heroPrimary.url}
          newTab={heroPrimary.newTab}
          onTextChange={(v) => updateBtn(setHeroPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setHeroPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setHeroPrimary, "newTab", v)}
        />
        <CTAGroup
          label="Secondary CTA"
          text={heroSecondary.text}
          url={heroSecondary.url}
          newTab={heroSecondary.newTab}
          onTextChange={(v) => updateBtn(setHeroSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setHeroSecondary, "url", v)}
          onNewTabChange={(v) => updateBtn(setHeroSecondary, "newTab", v)}
        />
      </CollapsibleSection>

      {/* WHY THYNKPROCESS */}
      <CollapsibleSection
        title="Why ThynkProcess"
        active={whyActive}
        onToggleActive={setWhyActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={whyBadgeText}
            onChange={(e) => setWhyBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={whyHeading}
            onChange={(e) => setWhyHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={whyHeadingEmphasis}
            onChange={(e) => setWhyHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={whyDescription}
            onChange={(e) => setWhyDescription(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Cards</p>
          <button
            onClick={() =>
              setWhyCards((s) => [
                ...s,
                { icon: "", title: "", description: "" },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Card
          </button>
        </div>
        {whyCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Card {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={whyCards.length}
                  onMove={(f, t) => setWhyCards(moveItem(whyCards, f, t))}
                />
                {whyCards.length > 1 && (
                  <button
                    onClick={() =>
                      setWhyCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Icon">
              <SelectInput
                value={c.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setWhyCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setWhyCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setWhyCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* CULTURE */}
      <CollapsibleSection
        title="Culture"
        active={cultureActive}
        onToggleActive={setCultureActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={cultureBadgeText}
            onChange={(e) => setCultureBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={cultureHeading}
            onChange={(e) => setCultureHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={cultureHeadingEmphasis}
            onChange={(e) => setCultureHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Section Image">
          <MediaImageField
            label="Select Image"
            value={cultureImage}
            onChange={setCultureImage}
          />
        </Field>
        <Field label="Image Alt Text">
          <TextInput
            value={cultureImageAlt}
            onChange={(e) => setCultureImageAlt(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Paragraphs</p>
          <button
            onClick={() => setCultureParagraphs((s) => [...s, ""])}
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Paragraph
          </button>
        </div>
        {cultureParagraphs.map((p, i) => (
          <div key={i} className="flex gap-2 items-start mb-2">
            <div className="flex-1">
              <Field label={`Paragraph ${i + 1}`}>
                <TextArea
                  value={p}
                  onChange={(e) =>
                    setCultureParagraphs((s) =>
                      s.map((x, idx) => (idx === i ? e.target.value : x)),
                    )
                  }
                />
              </Field>
            </div>
            {cultureParagraphs.length > 1 && (
              <button
                className="mt-6"
                onClick={() =>
                  setCultureParagraphs((s) => s.filter((_, idx) => idx !== i))
                }
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </CollapsibleSection>

      {/* HIRING PROCESS */}
      <CollapsibleSection
        title="Hiring Process"
        active={hiringActive}
        onToggleActive={setHiringActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={hiringBadgeText}
            onChange={(e) => setHiringBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={hiringHeading}
            onChange={(e) => setHiringHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={hiringHeadingEmphasis}
            onChange={(e) => setHiringHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextInput
            value={hiringDescription}
            onChange={(e) => setHiringDescription(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Steps</p>
          <button
            onClick={() => {
              const next = String(hiringSteps.length + 1).padStart(2, "0");
              setHiringSteps((s) => [
                ...s,
                { step: next, title: "", description: "" },
              ]);
            }}
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Step
          </button>
        </div>
        {hiringSteps.map((s, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Step {s.step}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={hiringSteps.length}
                  onMove={(f, t) => setHiringSteps(moveItem(hiringSteps, f, t))}
                />
                {hiringSteps.length > 1 && (
                  <button
                    onClick={() =>
                      setHiringSteps((st) =>
                        st
                          .filter((_, idx) => idx !== i)
                          .map((x, idx) => ({
                            ...x,
                            step: String(idx + 1).padStart(2, "0"),
                          })),
                      )
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={s.title}
                onChange={(e) =>
                  setHiringSteps((st) =>
                    st.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={s.description}
                onChange={(e) =>
                  setHiringSteps((st) =>
                    st.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* APPLY SECTION */}
      <CollapsibleSection
        title="Apply Section Header"
        active={applyActive}
        onToggleActive={setApplyActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={applyBadgeText}
            onChange={(e) => setApplyBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={applyHeading}
            onChange={(e) => setApplyHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={applyHeadingEmphasis}
            onChange={(e) => setApplyHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={applyDescription}
            onChange={(e) => setApplyDescription(e.target.value)}
          />
        </Field>
      </CollapsibleSection>

      {/* SIDEBAR */}
      <CollapsibleSection
        title="Form Sidebar"
        active={sidebarActive}
        onToggleActive={setSidebarActive}
      >
        <p className="text-xs font-semibold text-muted-foreground mb-3">
          Careers Contact Card
        </p>
        <Field label="Card Heading">
          <TextInput
            value={sidebarCareersHeading}
            onChange={(e) => setSidebarCareersHeading(e.target.value)}
          />
        </Field>
        <Field label="Card Subtext">
          <TextInput
            value={sidebarCareersSubtext}
            onChange={(e) => setSidebarCareersSubtext(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email">
            <TextInput
              value={sidebarEmail}
              onChange={(e) => setSidebarEmail(e.target.value)}
            />
          </Field>
          <Field label="Email Sub Label">
            <TextInput
              value={sidebarEmailSub}
              onChange={(e) => setSidebarEmailSub(e.target.value)}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone">
            <TextInput
              value={sidebarPhone}
              onChange={(e) => setSidebarPhone(e.target.value)}
            />
          </Field>
          <Field label="Phone Sub Label">
            <TextInput
              value={sidebarPhoneSub}
              onChange={(e) => setSidebarPhoneSub(e.target.value)}
            />
          </Field>
        </div>

        <p className="text-xs font-semibold text-muted-foreground mt-5 mb-3">
          What We Offer
        </p>
        <Field label="Card Heading">
          <TextInput
            value={sidebarOffersHeading}
            onChange={(e) => setSidebarOffersHeading(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-3 mb-2">
          <p className="text-xs font-semibold">Offer Items</p>
          <button
            onClick={() => setSidebarOfferItems((s) => [...s, ""])}
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Item
          </button>
        </div>
        {sidebarOfferItems.map((item, i) => (
          <div key={i} className="flex gap-2 items-center mb-2">
            <div className="flex-1">
              <TextInput
                value={item}
                onChange={(e) =>
                  setSidebarOfferItems((s) =>
                    s.map((x, idx) => (idx === i ? e.target.value : x)),
                  )
                }
              />
            </div>
            {sidebarOfferItems.length > 1 && (
              <button
                onClick={() =>
                  setSidebarOfferItems((s) => s.filter((_, idx) => idx !== i))
                }
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}

        <p className="text-xs font-semibold text-muted-foreground mt-5 mb-3">
          Speculative Application Box
        </p>
        <Field label="Heading">
          <TextInput
            value={sidebarSpecHeading}
            onChange={(e) => setSidebarSpecHeading(e.target.value)}
          />
        </Field>
        <Field label="Text">
          <TextArea
            value={sidebarSpecText}
            onChange={(e) => setSidebarSpecText(e.target.value)}
          />
        </Field>
        <Field label="Button Text">
          <TextInput
            value={sidebarSpecCTA}
            onChange={(e) => setSidebarSpecCTA(e.target.value)}
          />
        </Field>
      </CollapsibleSection>

      {/* FINAL CTA */}
      <CollapsibleSection
        title="Final CTA"
        active={ctaActive}
        onToggleActive={setCtaActive}
      >
        <Field label="Heading">
          <TextInput
            value={ctaHeading}
            onChange={(e) => setCtaHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={ctaHeadingEmphasis}
            onChange={(e) => setCtaHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={ctaDescription}
            onChange={(e) => setCtaDescription(e.target.value)}
          />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={ctaPrimary.text}
          url={ctaPrimary.url}
          newTab={ctaPrimary.newTab}
          onTextChange={(v) => updateBtn(setCtaPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaPrimary, "newTab", v)}
        />
        <CTAGroup
          label="Secondary CTA"
          text={ctaSecondary.text}
          url={ctaSecondary.url}
          newTab={ctaSecondary.newTab}
          onTextChange={(v) => updateBtn(setCtaSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaSecondary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaSecondary, "newTab", v)}
        />
      </CollapsibleSection>
    </>
  );
}
